import {
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useState } from "react";
import api from "../../../../../services/api";
import { sampleReportKeys } from "../../../queryKeys";
import { isSampleAnalyzingOnRlcs } from "../../../util";

type AnalysisResponse = {
  display_status: string | null;
  analysis_status: string | null;
};

type StatusResponse = {
  automated: AnalysisResponse;
  interactive: AnalysisResponse;
};

type ResponseData = AnalysisResponse & { analyzing: boolean };
export interface RLcsAnalysisResponseData {
  automated: ResponseData;
  interactive: ResponseData;
}

interface RlcsAnalysisStatusProps {
  hash: string;
  refetchMergedReportFn: () => void;
  pollDisabled: boolean;
  options?: UseQueryOptions;
}

export const getRLCSAnalysisStatusQueryKey = (hash: string) =>
  sampleReportKeys.analysisStatus(hash);

const DEFAULT_REFETCH_INTERVAL = 15000;

const INITIAL_DATA: ResponseData = {
  analyzing: false,
  analysis_status: null,
  display_status: null
};

export const useRlcsAnalysisStatus = ({
  hash,
  refetchMergedReportFn,
  pollDisabled,
  options
}: RlcsAnalysisStatusProps) => {
  const [enabled, setEnabled] = useState(!pollDisabled);
  const queryClient = useQueryClient();

  const analysisStatusQueryKey = getRLCSAnalysisStatusQueryKey(hash);
  const {
    data,
    refetch: refetchAnalysisStatus,
    isPending: isAnalysisStatusLoading
  } = useQuery({
    initialData: {
      automated: INITIAL_DATA,
      interactive: INITIAL_DATA
    },
    queryKey: sampleReportKeys.analysisStatus(hash),
    queryFn: () =>
      api
        .rlSandboxDisplayStatus([hash])
        .then((response: AxiosResponse<StatusResponse>) => {
          const { interactive, automated } = response.data;
          const { display_status: interactiveStatus } = interactive;
          const { display_status: automatedStatus } = automated;

          const isAutomatedAnalyzing = isSampleAnalyzingOnRlcs(automatedStatus);
          const isInteractiveAnalyzing =
            isSampleAnalyzingOnRlcs(interactiveStatus);

          const prev = queryClient.getQueryData<StatusResponse>(
            analysisStatusQueryKey
          );

          const isPrevAutomatedAnalyzing = isSampleAnalyzingOnRlcs(
            prev?.automated?.display_status
          );
          const isPrevInteractiveAnalyzing = isSampleAnalyzingOnRlcs(
            prev?.interactive.display_status
          );

          const isAutomatedFinished =
            isPrevAutomatedAnalyzing && !isAutomatedAnalyzing;
          const isInteractiveFinished =
            isPrevInteractiveAnalyzing && !isInteractiveAnalyzing;

          const shouldRefetchReport =
            isAutomatedFinished || isInteractiveFinished;
          const shouldDisablePoll =
            isAutomatedFinished && isInteractiveFinished;

          setEnabled(!pollDisabled ? !shouldDisablePoll : false);
          if (shouldRefetchReport) refetchMergedReportFn();

          const enrichedAutomated = {
            ...automated,
            analyzing: isAutomatedAnalyzing
          };
          const enrichedInteractive = {
            ...interactive,
            analyzing: isInteractiveAnalyzing
          };

          return {
            interactive: enrichedInteractive,
            automated: enrichedAutomated
          };
        }),
    enabled,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    ...options
  });

  return {
    analysisStatusData: data as RLcsAnalysisResponseData | undefined,
    isAnalysisStatusLoading,
    refetchAnalysisStatus,
    analysisStatusQueryKey
  };
};
